import { Room, RoomOptions, VideoPresets, createLocalVideoTrack, createLocalAudioTrack } from 'livekit-client';
import { api } from './api';

export interface MediaPermissions {
  camera: boolean;
  microphone: boolean;
}

export class PermissionError extends Error {
  constructor(message: string, public permissions: MediaPermissions) {
    super(message);
    this.name = 'PermissionError';
  }
}

export interface LiveKitConfig {
  serverUrl: string;
  apiKey: string;
  apiSecret: string;
}

export class LiveKitService {
  private static instance: LiveKitService;
  private config: LiveKitConfig;
  private room: Room | null = null;

  constructor() {
    // Using the credentials from the memory - LiveKit server setup
    this.config = {
      serverUrl: process.env.REACT_APP_LIVEKIT_URL!,
      apiKey: '6vE6WbEegZ1Ytsudx3g1FQ',
      apiSecret: 'rQYIJEXtGdmNYODq700ROs9LelrhD7Cg-_bKC4MDrTw'
    };
  }

  static getInstance(): LiveKitService {
    if (!LiveKitService.instance) {
      LiveKitService.instance = new LiveKitService();
    }
    return LiveKitService.instance;
  }

  /**
   * Check if camera and microphone permissions are granted
   * @returns Promise<MediaPermissions> Object containing permission status for camera and microphone
   */
  async checkMediaPermissions(): Promise<MediaPermissions> {
    const permissions: MediaPermissions = {
      camera: false,
      microphone: false
    };

    try {
      // Try to access camera
      // Try to access camera
      try {
        const videoTrack = await createLocalVideoTrack({
          deviceId: undefined, // Use default device
          resolution: VideoPresets.h720.resolution
        });
        permissions.camera = true;
        // Stop the temporary track
        videoTrack.stop();
      } catch (error) {
        console.warn('Camera permission denied or not available:', error);
        permissions.camera = false;
      }

      // Try to access microphone
      // Try to access microphone
      try {
        const audioTrack = await createLocalAudioTrack({
          deviceId: undefined // Use default device
        });
        permissions.microphone = true;
        // Stop the temporary track
        audioTrack.stop();
      } catch (error) {
        console.warn('Microphone permission denied or not available:', error);
        permissions.microphone = false;
      }
    } catch (error) {
      console.error('Error checking media permissions:', error);
    }

    return permissions;
  }

  async generateToken(roomName: string, participantName: string): Promise<string> {
    try {
      // In a real application, this should be done on the backend
      // Using axios with automatic token handling via interceptors
      const response = await api.post('/meetings/token', {
        room: roomName,
        identity: participantName
      });

      return response.data.data.token;
    } catch (error) {
      console.error('Error generating token:', error);
      throw error;
    }
  }

  async connectToRoom(
    roomName: string,
    participantName: string,
    token?: string,
    videoQuality?: string,
    facingMode?: 'user' | 'environment'
  ): Promise<Room> {
    try {
      // Note: Media permissions are now handled in the onboarding flow
      // Users must enable at least one device (camera or mic) before joining

      // Generate token if not provided
      const accessToken = token || await this.generateToken(roomName, participantName);

      // Determine video resolution based on quality setting
      let videoResolution = VideoPresets.h720.resolution;
      if (videoQuality === '1080p') {
        videoResolution = VideoPresets.h1080.resolution;
      } else if (videoQuality === '720p') {
        videoResolution = VideoPresets.h720.resolution;
      } else if (videoQuality === '360p') {
        videoResolution = VideoPresets.h360.resolution;
      } else if (videoQuality === '240p') {
        videoResolution = VideoPresets.h180.resolution;
      }

      // Create room instance with enhanced WebRTC configuration
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: videoResolution,
          facingMode: facingMode || 'user',
        },
        // Add WebRTC configuration for better connectivity
        rtcConfig: {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' },
          ],
          iceTransportPolicy: 'all',
          bundlePolicy: 'max-bundle',
          rtcpMuxPolicy: 'require',
          iceCandidatePoolSize: 10,
        },
        // Enable experimental features for better connectivity
        expWebAudioMix: false,
        disconnectOnPageLeave: true,
      } as RoomOptions);

      console.log('Attempting to connect to LiveKit room:', roomName, 'with video settings:', { videoQuality, facingMode });

      // Connect to the room with timeout
      const connectPromise = this.room.connect(this.config.serverUrl, accessToken);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Connection timeout after 30 seconds')), 30000)
      );

      await Promise.race([connectPromise, timeoutPromise]);

      console.log('Connected to LiveKit room:', roomName);
      return this.room;
    } catch (error) {
      console.error('Failed to connect to room:', error);

      // Clean up room on failure
      if (this.room) {
        try {
          await this.room.disconnect();
        } catch (disconnectError) {
          console.error('Error disconnecting failed room:', disconnectError);
        }
        this.room = null;
      }

      throw error;
    }
  }

  async enableCameraAndMicrophone(): Promise<void> {
    if (!this.room) {
      throw new Error('Room not connected');
    }

    try {
      // Request media permissions first
      // Request media permissions first
      const videoTrack = await createLocalVideoTrack({
        deviceId: undefined,
        resolution: VideoPresets.h720.resolution
      });
      const audioTrack = await createLocalAudioTrack({
        deviceId: undefined
      });

      // Stop the temporary tracks
      videoTrack.stop();
      audioTrack.stop();

      // Now enable LiveKit media
      await this.room.localParticipant.enableCameraAndMicrophone();
      console.log('Camera and microphone enabled');
    } catch (error) {
      console.error('Failed to enable camera and microphone:', error);
      // Try to enable just microphone if camera fails
      try {
        await this.room.localParticipant.setMicrophoneEnabled(true);
        console.log('Microphone enabled (camera failed)');
      } catch (micError) {
        console.error('Failed to enable microphone:', micError);
      }
      throw error;
    }
  }

  async toggleCamera(): Promise<boolean> {
    if (!this.room) return false;

    try {
      const enabled = this.room.localParticipant.isCameraEnabled;
      await this.room.localParticipant.setCameraEnabled(!enabled);
      return !enabled;
    } catch (error) {
      console.error('Failed to toggle camera:', error);
      throw error;
    }
  }

  async toggleMicrophone(): Promise<boolean> {
    if (!this.room) return false;

    try {
      const enabled = this.room.localParticipant.isMicrophoneEnabled;
      await this.room.localParticipant.setMicrophoneEnabled(!enabled);
      return !enabled;
    } catch (error) {
      console.error('Failed to toggle microphone:', error);
      throw error;
    }
  }

  /**
   * Update video constraints (quality and facing mode)
   */
  async updateVideoConstraints(quality: string, facingMode: 'user' | 'environment'): Promise<void> {
    if (!this.room) {
      throw new Error('Room not connected');
    }

    try {
      const constraints: MediaTrackConstraints = {
        facingMode: facingMode,
      };

      // Apply resolution constraints based on quality setting
      if (quality === '720p') {
        constraints.width = { ideal: 1280 };
        constraints.height = { ideal: 720 };
      } else if (quality === '1080p') {
        constraints.width = { ideal: 1920 };
        constraints.height = { ideal: 1080 };
      } else if (quality === '360p') {
        constraints.width = { ideal: 640 };
        constraints.height = { ideal: 360 };
      } else if (quality === '240p') {
        constraints.width = { ideal: 426 };
        constraints.height = { ideal: 240 };
      }
      // 'auto' uses default constraints

      // Get current camera state
      const wasCameraEnabled = this.room.localParticipant.isCameraEnabled;

      // If camera is on, restart it with new constraints
      if (wasCameraEnabled) {
        // Import Track at the top if not already imported
        const { Track } = await import('livekit-client');

        // Get the current video track publication
        const videoPublication = this.room.localParticipant.getTrackPublication(Track.Source.Camera);

        // Unpublish the current camera track
        if (videoPublication?.track) {
          await this.room.localParticipant.unpublishTrack(videoPublication.track);
        }

        // Small delay to ensure track is fully stopped
        await new Promise(resolve => setTimeout(resolve, 200));

        // Create a new camera track with the new constraints
        const videoTrack = await createLocalVideoTrack({
          facingMode: constraints.facingMode as any,
          resolution: {
            width: (constraints.width as any)?.ideal || 1280,
            height: (constraints.height as any)?.ideal || 720
          }
        });

        // Publish the new track
        await this.room.localParticipant.publishTrack(videoTrack, {
          name: 'camera',
          source: Track.Source.Camera,

        });
      }

      console.log('Video constraints updated:', { quality, facingMode });
    } catch (error) {
      console.error('Failed to update video constraints:', error);
      throw error;
    }
  }

  /**
   * Update audio input device
   */
  async updateAudioInputDevice(deviceId: string): Promise<void> {
    if (!this.room) {
      throw new Error('Room not connected');
    }

    try {
      const wasMicEnabled = this.room.localParticipant.isMicrophoneEnabled;

      if (wasMicEnabled) {
        // Switch to new device
        await this.room.switchActiveDevice('audioinput', deviceId);
        console.log('Audio input device updated:', deviceId);
      }
    } catch (error) {
      console.error('Failed to update audio input device:', error);
      throw error;
    }
  }

  /**
   * Update audio output device
   */
  async updateAudioOutputDevice(deviceId: string): Promise<void> {
    if (!this.room) {
      throw new Error('Room not connected');
    }

    try {
      // Try to use LiveKit's built-in method first
      await this.room.switchActiveDevice('audiooutput', deviceId);
      console.log('Audio output device updated via LiveKit:', deviceId);
    } catch (error) {
      console.warn('LiveKit switchActiveDevice failed, trying manual setSinkId:', error);

      // Fallback: manually set sinkId on all audio elements
      // This is handled in ParticipantTile component via the store
      // Just log the attempt here
      console.log('Audio output device will be applied via ParticipantTile components');
    }
  }

  /**
   * Check if screen sharing is supported on this device/browser
   */
  isScreenShareSupported(): boolean {
    // Check if getDisplayMedia is available
    const hasGetDisplayMedia = !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getDisplayMedia
    );

    if (!hasGetDisplayMedia) {
      return false;
    }

    // iOS devices don't support screen sharing at all
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      return false;
    }

    // Some Android browsers support it, but it's limited
    // We'll allow the attempt but the error handling will catch issues
    return hasGetDisplayMedia;
  }

  async toggleScreenShare(): Promise<boolean> {
    if (!this.room) return false;

    // Note: We no longer check isScreenShareSupported() upfront
    // Instead, we attempt screen sharing and let the error handler catch any issues
    // This allows it to work on devices where our detection fails but the feature actually works

    try {
      const isSharing = this.room.localParticipant.isScreenShareEnabled;
      await this.room.localParticipant.setScreenShareEnabled(!isSharing);
      return !isSharing;
    } catch (error) {
      console.error('Failed to toggle screen share:', error);
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    if (this.room) {
      await this.room.disconnect();
      this.room = null;
      console.log('Disconnected from LiveKit room');
    }
  }

  getRoom(): Room | null {
    return this.room;
  }

  isConnected(): boolean {
    return this.room?.state === 'connected';
  }
}

export const liveKitService = LiveKitService.getInstance();
